import React, {useEffect, useState, useRef} from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Zoom from '@mui/material/Zoom';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PropTypes from 'prop-types';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom in ref={ref} {...props} />;
});

const AppMedialViewer = ({index, medias, onClose}) => {
  const [isOpen, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const imageRef = useRef(null);

  useEffect(() => {
    if (index > -1 && medias && medias.length > 0) {
      setOpen(true);
      setCurrentIndex(Math.min(index, medias.length - 1));
    } else {
      setOpen(false);
      setCurrentIndex(-1);
    }
  }, [index, medias]);

  const handlePrevious = React.useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const handleNext = React.useCallback(() => {
    if (currentIndex < medias.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, medias.length]);

  const handleKeyDown = React.useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [isOpen, handlePrevious, handleNext, onClose],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!medias || medias.length === 0 || currentIndex < 0) {
    return null;
  }

  const currentMedia = medias[currentIndex];
  const mimeType = currentMedia?.mime_type || '';

  const renderMedia = () => {
    if (mimeType.startsWith('image')) {
      return (
        <img
          ref={imageRef}
          src={currentMedia.url}
          alt={currentMedia.name || 'Image preview'}
          style={{
            maxWidth: '100%',
            maxHeight: '90vh',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto',
          }}
        />
      );
    } else if (mimeType.startsWith('video')) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            maxHeight: '90vh',
          }}
        >
          <video
            src={currentMedia.url}
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
            }}
          />
        </Box>
      );
    } else if (mimeType.startsWith('audio')) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            padding: 5,
          }}
        >
          <audio
            src={currentMedia.url}
            controls
            style={{width: '80%', maxWidth: '600px'}}
          />
        </Box>
      );
    } else if (mimeType.includes('pdf')) {
      return (
        <iframe
          src={currentMedia.url}
          title={currentMedia.name || 'PDF preview'}
          style={{
            width: '100%',
            height: '90vh',
            border: 'none',
          }}
        />
      );
    } else {
      return (
        <iframe
          src={currentMedia.url}
          title={currentMedia.name || 'Document preview'}
          style={{
            width: '100%',
            height: '90vh',
            border: 'none',
          }}
        />
      );
    }
  };

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paperFullScreen': {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
        },
      }}
      TransitionComponent={Transition}
    >
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 2,
        }}
      >
        {/* Close Button */}
        <IconButton
          sx={{
            color: 'white',
            position: 'absolute',
            right: 10,
            top: 10,
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          }}
          onClick={onClose}
          size='large'
        >
          <HighlightOffIcon />
        </IconButton>

        {/* Previous Button */}
        {medias.length > 1 && currentIndex > 0 && (
          <IconButton
            sx={{
              color: 'white',
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
            onClick={handlePrevious}
            size='large'
          >
            <NavigateBeforeIcon />
          </IconButton>
        )}

        {/* Next Button */}
        {medias.length > 1 && currentIndex < medias.length - 1 && (
          <IconButton
            sx={{
              color: 'white',
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
            onClick={handleNext}
            size='large'
          >
            <NavigateNextIcon />
          </IconButton>
        )}

        {/* Media Content */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {renderMedia()}
        </Box>

        {/* Counter */}
        {medias.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '8px 16px',
              borderRadius: 2,
              fontSize: '0.875rem',
            }}
          >
            {currentIndex + 1} / {medias.length}
          </Box>
        )}

        {/* File Name */}
        {currentMedia.name && (
          <Box
            sx={{
              position: 'absolute',
              bottom: medias.length > 1 ? 60 : 20,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '8px 16px',
              borderRadius: 2,
              fontSize: '0.875rem',
              maxWidth: '80%',
              textAlign: 'center',
            }}
          >
            {currentMedia.name}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default AppMedialViewer;

AppMedialViewer.propTypes = {
  index: PropTypes.number,
  medias: PropTypes.array,
  onClose: PropTypes.func,
};
